/**
 * Helper Functions Module
 *
 * This module contains utility functions for node position management and other helpers.
 *
 * Key Functions:
 * - mergeNodesPosition: Merges computed positions with user-modified positions
 * - getColorFromItemStatus: Maps item status to color
 * - hasExpandedActions: Checks if item has expandable actions
 * - generateCollapsedPipelineItems: Generates collapsed view items
 * - getReferences: Gets referenced resources
 *
 * See docs/FlowNodesArchitecture.md for architecture overview.
 */

import keyBy from 'lodash/keyBy';
import { entityByKey, DATA_RETRIEVER_MIN_WIDTH } from './consts';
import { Node, XYPosition, Edge } from '@xyflow/react';
import { getRelativeNodesBounds } from './utils';
import { RESOURCE_TYPE_DATA_SET, RESOURCE_TYPE_SINK } from 'js/constants/resourceTypes';

/**
 * Color mapping for different item statuses.
 * Used for visual indicators in the flow canvas.
 */
export const colors = {
  RELATION: '#5DB523',
  DEPENDENCY: 'var(--color-secondary-600)',
  ACTIVE: 'var(--color-primary-600)',
  PROCESSING: 'var(--color-primary-600)',
  PAUSED: 'var(--color-warning-600)',
  RATE_LIMITED: 'var(--color-danger-600)',
  INIT: 'var(--color-greyscale-600)',
};

/**
 * Merges computed node positions with user-modified positions to preserve manual changes.
 *
 * This function is critical for preventing nodes from "jumping" back to their computed
 * positions when the canvas re-renders. It intelligently preserves positions for nodes
 * that users have manually moved while allowing computed positions for nodes that haven't
 * been moved.
 *
 * Position Preservation Rules (in order of evaluation):
 *
 * 1. Secondary Flows: Preserve if user-moved, otherwise use computed positions
 *    - Secondary flows are positioned deterministically below main flow by default
 *    - If user manually moves a secondary flow, preserve that position
 *    - This prevents positions from resetting when modals open or canvas re-renders
 *
 * 2. Flow Expansion State: If expansion state changed, preserve position
 *    - When a flow expands/collapses, preserve positions to prevent jumping
 *    - This handles the case where nodes shift when flow container size changes
 *
 * 3. Flow Nodes: Flow container nodes themselves are not preserved
 *    - Flow nodes (containers) use computed positions
 *    - Only resource nodes inside flows can have preserved positions
 *
 * 4. Flow Container Tracking: Only preserve if container has user-moved children
 *    - If a flow container has children that were moved by user, preserve all children
 *    - If container has no user-moved children, use computed positions
 *    - This prevents preserving positions for entire flows when only one node moved
 *
 * 5. Reference Nodes: Reference nodes always use computed positions
 *    - References are dynamically positioned and shouldn't be preserved
 *
 * 6. Project Nodes: Project nodes are not preserved
 *    - Projects use computed positions
 *
 * 7. Default: Preserve position if source node exists
 *    - If node existed before and wasn't filtered by above rules, preserve its position
 *
 * How It Works:
 * 1. Creates a map of previous nodes by ID for quick lookup
 * 2. Tracks which flow containers have user-moved children
 * 3. For each new node, applies preservation rules in order
 * 4. Returns nodes with either preserved or computed positions
 *
 * @param srcNodes - Previous nodes array (before re-render)
 * @param newNodes - New nodes array (after layout computation, will be mutated)
 * @param changedNodes - Optional map of nodes moved by user: { nodeId: { flowNode, position } }
 * @param edges - Optional edges array (used for post-merge adjustments)
 * @param options - Optional. useComputedPositionsOnly: when true (e.g. layout direction just changed), skip all preservation and use Dagre-computed positions for a full rearrangement
 * @returns New nodes array with positions merged (mutated in place)
 *
 * @example
 * // User moves a node, then flow expands
 * const changedNodes = { 'node-123': { flowNode: {...}, position: { x: 100, y: 200 } } };
 * const merged = mergeNodesPosition(oldNodes, newNodes, changedNodes);
 * // Node 'node-123' will have position { x: 100, y: 200 } preserved
 *
 * @see docs/FlowNodesArchitecture.md - Position preservation section
 * @see docs/FlowNodesModificationGuide.md - How to modify preservation logic
 */
export function mergeNodesPosition(
  srcNodes: Node<{ item: any; expanded: boolean }>[],
  newNodes: Node<{ item: any; expanded: boolean }>[],
  changedNodes?: Record<string, { flowNode: any; position: XYPosition }>,
  edges?: Edge[],
  options?: { useComputedPositionsOnly?: boolean },
) {
  if (options?.useComputedPositionsOnly) {
    return newNodes.map((node) => ({ ...node }));
  }

  // Create a lookup map of previous nodes for O(1) access
  const sourceMap = keyBy(srcNodes, 'id');

  /**
   * Track which flow containers have user-moved children.
   *
   * If a flow container has any children that were moved by the user, we preserve
   * positions for ALL children in that container. This prevents partial preservation
   * which could cause visual inconsistencies.
   */
  const changedFlowContainerIds = new Set<string>();

  if (changedNodes) {
    Object.keys(changedNodes).forEach((nodeId) => {
      const previousNode = sourceMap[nodeId];
      // If the moved node has a parent (is inside a flow container), track the container
      if (previousNode?.parentId) {
        changedFlowContainerIds.add(previousNode.parentId);
      }
    });
  }

  return newNodes.map((node) => {
    const sourceNode = sourceMap[node.id];
    // Find the flow container nodes (parent flows) in both old and new node arrays
    const sourceFlowNode = srcNodes.find((s) => s.id === sourceNode?.parentId);
    const flowNode = newNodes.find((s) => s.id === node?.parentId);

    // Log enricher position preservation decisions
    // if (node.type === 'context_enricher') {
    //   console.log('[POSITION_PRESERVATION] Context Enricher - checking rules:', {
    //     nodeId: node.id,
    //     computedX: node.position.x,
    //     computedY: node.position.y,
    //     hasSourceNode: !!sourceNode,
    //     sourceNodeX: sourceNode?.position.x,
    //     sourceNodeY: sourceNode?.position.y,
    //     parentId: node.parentId,
    //     nodeType: node.type
    //   });
    // }

    /**
     * RULE 1: Secondary flows - preserve if user-moved, otherwise use computed positions
     *
     * Secondary flows are positioned deterministically below the main flow by default.
     * However, if a user manually moves a secondary flow, we should preserve that position
     * to prevent it from resetting when modals open or the canvas re-renders.
     *
     * This fixes the issue where secondary flow positions reset when modals are opened.
     */
    // Schedule control nodes: On the very first render (no previous nodes),
    // use computed positions. On subsequent re-renders, preserve the position
    // from the previous render to prevent jitter caused by slight dagre
    // recalculations when node dimensions change after DOM measurement.
    // Layout direction changes use useComputedPositionsOnly which bypasses this entirely.
    if (node.type === 'last_run' || node.type === 'cron_job') {
      if (sourceNode) {
        // Node existed before — preserve its position to prevent jitter
        node.position = { ...sourceNode.position };
      }
      // First render (no sourceNode) — use computed position from dagre
      return node;
    }

    if (
      (node as any)?.data?.isSecondaryFlow === true ||
      flowNode?.type === 'secondary_flow' ||
      node.type === 'secondary_flow'
    ) {
      // Check if user has moved this secondary flow
      const inChangedNodes = changedNodes && changedNodes[node.id];
      if (inChangedNodes) {
        // Preserve the user's position (they explicitly dragged it)
        // Use position from changedNodes (user's explicit position) or fall back to sourceNode
        node.position =
          inChangedNodes.position || (sourceNode ? { ...sourceNode.position } : node.position);
        return node;
      }
      // Use computed position if not moved by user
      return node;
    }

    /**
     * RULE 2: Preserve position if flow expansion state changed
     *
     * When a flow expands or collapses, the container size changes. If we applied
     * computed positions here, nodes would jump. Instead, we preserve their positions
     * to maintain visual stability during expansion/collapse.
     */
    if (sourceFlowNode?.data?.expanded !== flowNode?.data?.expanded) {
      // console.log('[POSITION_PRESERVATION] RULE 2 triggered - expansion state changed:', {
      //   nodeId: node.id,
      //   wasExpanded: sourceFlowNode?.data?.expanded,
      //   nowExpanded: flowNode?.data?.expanded,
      //   preserving: node.position
      // });
      return node; // Preserve position (don't apply computed)
    }

    /**
     * RULE 3: Flow container nodes - preserve if user-moved
     *
     * For independent flows in multi-flow views, preserve position if:
     * 1. The flow was explicitly in changedNodes (just dragged), OR
     * 2. The source position differs from computed position (user moved it before)
     *
     * CRITICAL: For horizontal layout of independent flows, we should NOT preserve
     * positions based on positionChanged alone, because the layout algorithm
     * intentionally recomputes positions. Only preserve if explicitly in changedNodes.
     *
     * This allows users to reposition flows without them jumping back on collapse/expand.
     */
    if (node.type.includes('flow')) {
      if (sourceNode) {
        // Check if position changed from computed (user moved it)
        const positionChanged =
          sourceNode.position.x !== node.position.x || sourceNode.position.y !== node.position.y;

        // CRITICAL FIX: Only preserve if explicitly in changedNodes (user dragged it)
        // Don't preserve just because position changed - the layout algorithm intentionally
        // recomputes positions for horizontal layout, and we want those new positions
        const inChangedNodes = changedNodes && changedNodes[node.id];
        if (inChangedNodes) {
          // Preserve the user's position (they explicitly dragged it)
          node.position = { ...sourceNode.position };
          return node;
        }
        // If position changed but NOT in changedNodes, it's a layout recomputation
        // Use the computed position (don't preserve)
      }
      return node; // Use computed position
    }

    /**
     * RULE 4: Children inside retriever holders should ALWAYS use computed positions
     *
     * When new retriever flows are added, children inside the retriever holder
     * should be repositioned. Preserving their positions causes "messed up" layouts.
     */
    if (node.parentId) {
      const parentNode = newNodes.find((n) => n.id === node.parentId);
      if (parentNode?.type === 'data_retriever_holder') {
        // Always use computed position for children inside retriever holders
        // This ensures proper layout when new retrievers are added
        return node; // Use computed position (child of retriever holder)
      }
    }

    /**
     * RULE 5: Nodes following retriever holders should ALWAYS use computed positions
     *
     * Nodes that follow retriever holders (like context enricher) should always use
     * computed positions to maintain proper spacing. This is because:
     * 1. The retriever holder's width can change dynamically (source added, nexset detected)
     * 2. Edge adjustment logic positions these nodes correctly after layout
     * 3. Preserving position would override the edge adjustment, causing long edges
     *
     * We detect this by checking if:
     * 1. Node is in root group (not a child)
     * 2. There's a retriever holder before this node (position-based check)
     */
    if (sourceNode && !node.parentId && node.type !== 'last_run' && node.type !== 'cron_job') {
      // Find retriever holders in the new nodes
      const retrieverHolders = newNodes.filter((n) => n.type === 'data_retriever_holder');

      for (const retrieverHolder of retrieverHolders) {
        // Check if this node follows this retriever holder
        // (position-based check - if node is to the right, it likely follows)
        if (retrieverHolder.position.x < node.position.x) {
          // Always use computed position for nodes following retriever holders
          // This ensures edge adjustment logic works correctly
          // if (node.type === 'context_enricher') {
          //   console.log('[POSITION_PRESERVATION] Context Enricher - RULE 5 applied:', {
          //     nodeId: node.id,
          //     retrieverHolderId: retrieverHolder.id,
          //     retrieverHolderX: retrieverHolder.position.x,
          //     enricherComputedX: node.position.x,
          //     decision: 'Using computed position (follows retriever holder)',
          //     reason: 'Edge adjustment will position correctly after this'
          //   });
          // }
          return node; // Use computed position (follows retriever holder)
        }
      }
    }

    /**
     * RULE 6: Only preserve if flow container has user-moved children
     *
     * If the flow container doesn't have any user-moved children, we use computed
     * positions for all nodes in that container. This prevents preserving positions
     * for entire flows when the user hasn't moved anything.
     */
    if (changedNodes && flowNode && !changedFlowContainerIds.has(flowNode.id)) {
      return node; // Use computed position (container has no user-moved children)
    }

    /**
     * RULE 7 & 8: Preserve position for non-project, non-reference nodes
     *
     * If we've passed all the above rules and the node existed before, preserve
     * its position. This handles the case where a user moved a node and we want
     * to keep it in that position.
     *
     * Exceptions:
     * - Project nodes: Use computed positions
     * - Reference nodes: Use computed positions (they're dynamically positioned)
     */
    if (sourceNode && node.type !== 'project' && !node?.data?.item?.isReference) {
      // Preserve the original position
      // if (node.type === 'context_enricher') {
      //   console.log('[POSITION_PRESERVATION] Context Enricher - RULE 7/8 applied:', {
      //     nodeId: node.id,
      //     computedX: node.position.x,
      //     preservedX: sourceNode.position.x,
      //     computedY: node.position.y,
      //     preservedY: sourceNode.position.y,
      //     decision: 'Preserving user-moved position',
      //     warning: 'This might override edge adjustment!',
      //     difference: sourceNode.position.x - node.position.x
      //   });
      // }
      node.position = {
        ...sourceNode.position,
      };
    }
    return node;
  });

  /**
   * Post-merge adjustment: Fix edge length for nodes following retriever holders
   *
   * After position merging, we need to ensure nodes following retriever holders
   * are positioned correctly. This adjustment happens after merging so it can't
   * be overridden by position preservation.
   *
   * This fixes the "long edge" issue by positioning the enricher (or any node
   * following a retriever holder) at the correct distance from the container.
   *
   * We use edges to find the actual target node, not position-based detection,
   * to ensure we're adjusting the correct node.
   */
  if (edges && edges.length > 0) {
    const retrieverHolders = newNodes.filter((n) => n.type === 'data_retriever_holder');

    retrieverHolders.forEach((retrieverHolder) => {
      // Find edges that originate from this retriever holder
      const outgoingEdges = edges.filter((e) => e.source === retrieverHolder.id);

      outgoingEdges.forEach((edge) => {
        const targetNode = newNodes.find((n) => n.id === edge.target);

        if (
          targetNode &&
          // Only adjust if target is in the root group (not a child of another container)
          !targetNode.parentId
        ) {
          // Calculate runtime width based on actual child node positions
          // This is the "final runtime width" that should be honored
          const childNodes = newNodes.filter((n) => n.parentId === retrieverHolder.id);
          const rect = getRelativeNodesBounds(childNodes);
          const runtimeWidth = Math.max(rect.x + rect.width, DATA_RETRIEVER_MIN_WIDTH);

          // Use runtime width for positioning (this is the "final runtime width")
          const containerRightEdge = retrieverHolder.position.x + runtimeWidth;
          const standardSpacing = 60; // nodesep value - matches Dagre's horizontal spacing
          const desiredX = containerRightEdge + standardSpacing;

          // Also update node.data.width with runtime width so it persists
          const retrieverData = retrieverHolder.data as any;
          const storedWidth = retrieverData?.width as number;
          if (runtimeWidth !== storedWidth) {
            retrieverData.width = runtimeWidth;
            // console.log('[POST_MERGE_EDGE_ADJUST] Updated node.data.width with runtime width:', {
            //   nodeId: retrieverHolder.id,
            //   oldStoredWidth: storedWidth,
            //   newRuntimeWidth: runtimeWidth
            // });
          }

          // const isEnricher = targetNode.type === 'context_enricher';

          // if (isEnricher) {
          //   console.log('[ENRICHER_X_POSITION] ===== ENRICHER X POSITION TRACE =====');
          //   console.log('[ENRICHER_X_POSITION] Step 1 - Before adjustment:', {
          //     nodeId: targetNode.id,
          //     currentX: oldX,
          //     retrieverHolderId: retrieverHolder.id,
          //     retrieverHolderX: retrieverHolder.position.x,
          //     wasPreserved,
          //     sourceNodeX: srcNodes.find((s) => s.id === targetNode.id)?.position.x
          //   });
          // }

          // console.log('[POST_MERGE_EDGE_ADJUST] Using final runtime width:', {
          //   retrieverHolderId: retrieverHolder.id,
          //   retrieverHolderX: retrieverHolder.position.x,
          //   childNodesCount: childNodes.length,
          //   rectX: rect.x,
          //   rectWidth: rect.width,
          //   rectXPlusWidth: rect.x + rect.width,
          //   DATA_RETRIEVER_MIN_WIDTH,
          //   runtimeWidth,
          //   storedWidth,
          //   containerRightEdge,
          //   standardSpacing,
          //   desiredX,
          //   targetNodeId: targetNode.id,
          //   targetNodeType: targetNode.type,
          //   oldX,
          //   wasPreserved,
          //   adjustment: desiredX - oldX,
          //   formula: `${retrieverHolder.position.x} + ${runtimeWidth} + ${standardSpacing} = ${desiredX}`,
          //   note: 'Using final runtime width calculated from actual child positions'
          // });

          // Always adjust to ensure fixed edge length
          // This overrides any position preservation that might have happened
          targetNode.position.x = desiredX;

          // if (isEnricher) {
          //   console.log('[ENRICHER_X_POSITION] Step 2 - After adjustment:', {
          //     nodeId: targetNode.id,
          //     newX: desiredX,
          //     adjustment: desiredX - oldX,
          //     formula: `retrieverX(${retrieverHolder.position.x}) + runtimeWidth(${runtimeWidth}) + spacing(${standardSpacing}) = ${desiredX}`,
          //     edgeLength: desiredX - (retrieverHolder.position.x + runtimeWidth),
          //     note: 'This is the FINAL X position of the enricher'
          //   });
          //   console.log('[ENRICHER_X_POSITION] ===== END ENRICHER X POSITION TRACE =====');
          // }
        }
      });
    });
  }

  /**
   * CRITICAL: Preserve ReactFlow-managed node fields.
   *
   * When FlowNodes re-computes nodes (e.g. after opening/closing modals), we rebuild the
   * node objects. If we drop these fields, ReactFlow will continuously emit "dimensions"
   * changes, and in controlled mode StoreUpdater can fall into a setNodes → re-measure →
   * setNodes loop (Maximum update depth exceeded).
   *
   * CONDITIONAL PRESERVATION STRATEGY:
   * - Only preserve fields that are known safe and necessary to prevent the crash
   * - Skip preservation of complex objects (internals, handleBounds) that could cause
   *   stale layout cache issues
   * - Exception: Don't preserve width/height for nodes with dynamic widths (RAG retriever holders)
   *   as these need ReactFlow to re-measure when content changes
   */
  newNodes.forEach((node: any) => {
    const prev: any = sourceMap[node.id];
    if (!prev) return;

    // Preserve measured flag - this is safe and necessary to prevent dimension update loops
    if (prev.measured) {
      node.measured = prev.measured;
    }

    // Preserve width/height ONLY for nodes that don't dynamically resize
    const isDynamicWidthNode = node.type === 'data_retriever_holder';

    if (!isDynamicWidthNode) {
      if (typeof prev.width === 'number') {
        node.width = prev.width;
      }
      if (typeof prev.height === 'number') {
        node.height = prev.height;
      }
    }
  });

  return newNodes;
}

export function getColorFromItemStatus(status) {
  return colors[status] || colors.ACTIVE;
}

export function hasExpandedActions(item) {
  return item.type === 'SOURCE' && item.flow_node_id;
}

// This generates resources based on if the flow is collapsed or not
// If the flow is collapsed it means that if we have many nexsets and/or destinations
// We should only show the count of them.
// That's why we have a different nodeType for it "resourceCount"
export function generateCollapsedPipelineItems(item) {
  const source = item.items[item.origin_node_id];
  const destinations = Object.values(item.items).filter(
    (node: { [key: string]: any }) => node.type === 'DATA_SINK',
  ) as any[];
  const nexsets = Object.values(item.items).filter(
    (node: { [key: string]: any }) => node.type === 'DATA_SET',
  ) as any[];

  const nexset = (() => {
    if (nexsets.length >= 1) {
      return {
        nodeType: 'resourceCount',
        children: (() => {
          if (destinations.length) return [`${item.flow_node_id}-destination`];
          return [];
        })(),
        flow_node_id: `${item.flow_node_id}-nexset`,
        resourceType: RESOURCE_TYPE_DATA_SET,
        flow: item,
      };
    }
    return null;
  })();

  const nexsetId = (() => {
    if (nexsets.length >= 1) {
      return `${item.flow_node_id}-nexset`;
    }
    return null;
  })();

  const destination = (() => {
    if (destinations.length >= 1) {
      return {
        nodeType: 'resourceCount',
        flow_node_id: `${item.flow_node_id}-destination`,
        resourceType: RESOURCE_TYPE_SINK,
        flow: item,
      };
    }
    return null;
  })();

  const destinationId = (() => {
    if (destinations.length >= 1) {
      return `${item.flow_node_id}-destination`;
    }
    return null;
  })();

  const items = {
    [item.flow_node_id]: {
      ...source,
      children: nexsets.length >= 1 ? [`${item.flow_node_id}-nexset`] : source.children,
      size: 'small',
    },
  };

  if (nexsetId) {
    items[nexsetId] = nexset;

    if (destinationId) {
      items[destinationId] = destination;
    }
  }

  return items;
}

export function getReferencesByType(item, entities, resourceType) {
  const type = item.referenced_resource_ids[resourceType];
  if (type?.length) {
    const items = type
      .map((id) => {
        const item = entities[entityByKey[resourceType]]?.[id];
        if (item) {
          return {
            type: resourceType,
            isReference: true,
            ...item,
          };
        }
        return undefined;
      })
      .filter(Boolean);
    return items;
  }
  return [];
}

export function getReferences(item, entities) {
  if (!item?.referenced_resource_ids) return [];
  return Object.keys(item.referenced_resource_ids).reduce((refs, resourceKey) => {
    refs.push(...getReferencesByType(item, entities, resourceKey));
    return refs;
  }, []);
}
